import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ProvenanceModal = ({ isOpen, onClose, credit }) => {
  const [selectedNode, setSelectedNode] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(1);

  if (!isOpen || !credit) return null;

  // Mock provenance data
  const provenanceData = {
    nodes: [
      { id: 'issuance', type: 'issuance', label: 'Credit Issuance', date: '2023-01-15', registry: credit?.registry, status: 'completed' },
      { id: 'verification', type: 'verification', label: 'Third-Party Verification', date: '2023-01-10', verifier: credit?.verificationBody, status: 'completed' },
      { id: 'monitoring', type: 'monitoring', label: 'MRV Data Collection', date: '2022-12-01', provider: 'SatelliteVu', status: 'completed' },
      { id: 'transfer1', type: 'transfer', label: 'Registry Transfer', date: '2023-02-20', from: 'Project Developer', to: 'Carbon Trader A', status: 'completed' },
      { id: 'transfer2', type: 'transfer', label: 'Portfolio Transfer', date: '2023-06-15', from: 'Carbon Trader A', to: 'Corporate Buyer', status: 'completed' },
      { id: 'current', type: 'holding', label: 'Current Holding', date: '2023-06-15', holder: 'Global Carbon Corp', status: 'active' }
    ],
    connections: [
      { from: 'monitoring', to: 'verification' },
      { from: 'verification', to: 'issuance' },
      { from: 'issuance', to: 'transfer1' },
      { from: 'transfer1', to: 'transfer2' },
      { from: 'transfer2', to: 'current' }
    ]
  };

  const getNodeIcon = (type) => {
    switch (type) {
      case 'issuance': return 'Plus';
      case 'verification': return 'Shield';
      case 'monitoring': return 'Satellite';
      case 'transfer': return 'ArrowRightLeft';
      case 'holding': return 'Briefcase';
      default: return 'Circle';
    }
  };

  const getNodeColor = (type, status) => {
    if (status === 'active') return 'bg-primary text-primary-foreground';
    if (status === 'completed') return 'bg-success text-success-foreground';
    return 'bg-muted text-muted-foreground';
  };

  const handleZoomIn = () => setZoomLevel(prev => Math.min(prev + 0.2, 2));
  const handleZoomOut = () => setZoomLevel(prev => Math.max(prev - 0.2, 0.5));
  const handleResetZoom = () => setZoomLevel(1);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-card border border-border rounded-lg shadow-lg w-full max-w-6xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-4">
            <Icon name="GitBranch" size={24} className="text-primary" />
            <div>
              <h2 className="text-xl font-semibold text-card-foreground">Provenance Tracking</h2>
              <p className="text-sm text-muted-foreground font-mono">{credit?.serialNumber}</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Button variant="ghost" iconName="ZoomOut" onClick={handleZoomOut} />
            <Button variant="ghost" iconName="RotateCcw" onClick={handleResetZoom} />
            <Button variant="ghost" iconName="ZoomIn" onClick={handleZoomIn} />
            <Button variant="ghost" iconName="X" onClick={onClose} />
          </div>
        </div>

        <div className="flex h-[70vh]">
          {/* Provenance Graph */}
          <div className="flex-1 p-6 overflow-auto">
            <div 
              className="relative min-w-full min-h-full"
              style={{ transform: `scale(${zoomLevel})`, transformOrigin: 'top left' }}
            >
              {/* Timeline */}
              <div className="flex flex-col space-y-8">
                {provenanceData?.nodes?.map((node, index) => (
                  <div key={node?.id} className="relative">
                    {/* Connection Line */}
                    {index < provenanceData?.nodes?.length - 1 && (
                      <div className="absolute left-6 top-12 w-0.5 h-8 bg-border"></div>
                    )}
                    
                    {/* Node */}
                    <div 
                      className={`flex items-center space-x-4 p-4 rounded-lg border cursor-pointer transition-smooth ${
                        selectedNode?.id === node?.id 
                          ? 'border-primary bg-primary/5' :'border-border hover:border-muted-foreground hover:bg-muted/30'
                      }`}
                      onClick={() => setSelectedNode(node)}
                    >
                      <div className={`flex items-center justify-center w-12 h-12 rounded-full ${getNodeColor(node?.type, node?.status)}`}>
                        <Icon name={getNodeIcon(node?.type)} size={20} />
                      </div>
                      
                      <div className="flex-1">
                        <div className="font-medium text-card-foreground">{node?.label}</div>
                        <div className="text-sm text-muted-foreground">{node?.date}</div>
                        {node?.registry && (
                          <div className="text-xs text-muted-foreground">Registry: {node?.registry}</div>
                        )}
                        {node?.verifier && (
                          <div className="text-xs text-muted-foreground">Verifier: {node?.verifier}</div>
                        )}
                        {node?.provider && (
                          <div className="text-xs text-muted-foreground">Provider: {node?.provider}</div>
                        )}
                        {node?.from && node?.to && (
                          <div className="text-xs text-muted-foreground">{node?.from} → {node?.to}</div>
                        )}
                        {node?.holder && (
                          <div className="text-xs text-muted-foreground">Holder: {node?.holder}</div>
                        )}
                      </div>
                      
                      <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                        node?.status === 'active' ? 'bg-primary/10 text-primary' :
                        node?.status === 'completed'? 'bg-success/10 text-success' : 'bg-muted text-muted-foreground'
                      }`}>
                        {node?.status}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Details Panel */}
          <div className="w-80 border-l border-border p-6 bg-muted/20">
            {selectedNode ? (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-card-foreground mb-2">{selectedNode?.label}</h3>
                  <p className="text-sm text-muted-foreground">{selectedNode?.date}</p>
                </div>

                <div className="space-y-4">
                  {selectedNode?.type === 'issuance' && (
                    <>
                      <div>
                        <span className="text-sm text-muted-foreground">Registry:</span>
                        <div className="font-medium text-card-foreground">{selectedNode?.registry}</div>
                      </div>
                      <div>
                        <span className="text-sm text-muted-foreground">Serial Number:</span>
                        <div className="font-mono text-sm text-card-foreground">{credit?.serialNumber}</div>
                      </div>
                      <div>
                        <span className="text-sm text-muted-foreground">Quantity:</span>
                        <div className="font-medium text-card-foreground">{credit?.quantity?.toLocaleString()} tCO2e</div>
                      </div>
                    </>
                  )}

                  {selectedNode?.type === 'verification' && (
                    <>
                      <div>
                        <span className="text-sm text-muted-foreground">Verification Body:</span>
                        <div className="font-medium text-card-foreground">{selectedNode?.verifier}</div>
                      </div>
                      <div>
                        <span className="text-sm text-muted-foreground">Standard:</span>
                        <div className="font-medium text-card-foreground">{credit?.verificationStandard}</div>
                      </div>
                      <div>
                        <span className="text-sm text-muted-foreground">Report:</span>
                        <Button variant="link" size="sm" iconName="ExternalLink">
                          View Report
                        </Button>
                      </div>
                    </>
                  )}

                  {selectedNode?.type === 'monitoring' && (
                    <>
                      <div>
                        <span className="text-sm text-muted-foreground">Data Provider:</span>
                        <div className="font-medium text-card-foreground">{selectedNode?.provider}</div>
                      </div>
                      <div>
                        <span className="text-sm text-muted-foreground">Data Type:</span>
                        <div className="font-medium text-card-foreground">Satellite Imagery & IoT Sensors</div>
                      </div>
                      <div>
                        <span className="text-sm text-muted-foreground">Coverage:</span>
                        <div className="font-medium text-card-foreground">100% Project Area</div>
                      </div>
                    </>
                  )}

                  {selectedNode?.type === 'transfer' && (
                    <>
                      <div>
                        <span className="text-sm text-muted-foreground">From:</span>
                        <div className="font-medium text-card-foreground">{selectedNode?.from}</div>
                      </div>
                      <div>
                        <span className="text-sm text-muted-foreground">To:</span>
                        <div className="font-medium text-card-foreground">{selectedNode?.to}</div>
                      </div>
                      <div>
                        <span className="text-sm text-muted-foreground">Transfer Type:</span>
                        <div className="font-medium text-card-foreground">Registry Transfer</div>
                      </div>
                      <div>
                        <span className="text-sm text-muted-foreground">Transaction ID:</span>
                        <div className="font-mono text-xs text-card-foreground">TXN-{Math.random()?.toString(36)?.substr(2, 9)?.toUpperCase()}</div>
                      </div>
                    </>
                  )}

                  {selectedNode?.type === 'holding' && (
                    <>
                      <div>
                        <span className="text-sm text-muted-foreground">Current Holder:</span>
                        <div className="font-medium text-card-foreground">{selectedNode?.holder}</div>
                      </div>
                      <div>
                        <span className="text-sm text-muted-foreground">Account Type:</span>
                        <div className="font-medium text-card-foreground">Corporate Portfolio</div>
                      </div>
                      <div>
                        <span className="text-sm text-muted-foreground">Status:</span>
                        <div className="font-medium text-success">Available for Retirement</div>
                      </div>
                    </>
                  )}
                </div>

                {/* Blockchain Verification */}
                <div className="pt-4 border-t border-border">
                  <h4 className="font-medium text-card-foreground mb-3">Blockchain Verification</h4>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <Icon name="CheckCircle" size={16} className="text-success" />
                      <span className="text-sm text-card-foreground">Hash verified</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Icon name="CheckCircle" size={16} className="text-success" />
                      <span className="text-sm text-card-foreground">Timestamp confirmed</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Icon name="CheckCircle" size={16} className="text-success" />
                      <span className="text-sm text-card-foreground">Signature valid</span>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-center">
                <Icon name="GitBranch" size={48} className="text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium text-card-foreground mb-2">Select a Node</h3>
                <p className="text-sm text-muted-foreground">
                  Click on any node in the provenance graph to view detailed information about that step in the credit's lifecycle.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-border">
          <div className="text-sm text-muted-foreground">
            Provenance data verified via blockchain immutable ledger
          </div>
          <div className="flex items-center space-x-3">
            <Button variant="outline" iconName="Download">
              Export Graph
            </Button>
            <Button variant="default" iconName="Share">
              Share Provenance
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProvenanceModal;